/**
 * stackwright_pro_write_page — Pro-aware page write MCP tool.
 *
 * Wraps the OSS `writePage` validation with Pro content-type awareness:
 *
 *   - 24 Pro-only types (stats_grid, data_table_pulse, map_pulse, etc.) →
 *     replaced with a valid OSS stub before OSS validation runs.
 *   - 5 OSS types with Pro extensions (text_block, alert, grid, main,
 *     collection_list) → Pro augmentation keys (theme:, auth:,
 *     required_roles:) stripped, OSS fields validated normally.
 *
 * The ORIGINAL YAML string is always written to disk — never the sanitized
 * clone. js-yaml dump() doesn't preserve formatting and that's fine, because
 * we don't use it for the real write.
 *
 * Locale handling mirrors OSS stackwright_write_page: passing `locale`
 * writes content.<locale>.yml and never touches content.yml.
 */
import * as fs from 'fs';
import * as os from 'os';
import * as path from 'path';
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { load as yamlLoad, dump as yamlDump } from 'js-yaml';
import { writePage, resolvePagesDir } from '@stackwright/cli';
import { isProContentType, isOssTypeWithProExtension } from '@stackwright-pro/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BCP47_RE = /^[a-z]{2}(-[A-Z]{2})?$/;

/** Pro augmentation keys that strict OSS schemas reject. */
const PRO_EXTENSION_STRIP_KEYS = new Set(['theme', 'auth', 'required_roles']);

/**
 * Valid OSS text_block stub that stands in for Pro-only types during OSS
 * schema validation.
 *
 * Requirements (from OSS textBlockContentSchema):
 *   - label: string   (from baseContentSchema — required)
 *   - textBlocks: []  (z.array() with no min — empty array is valid)
 *   - heading omitted (optional; would need textSize if present)
 */
const PRO_TYPE_STUB: Record<string, unknown> = {
  type: 'text_block',
  label: '__pro_validation_stub__',
  textBlocks: [],
};

// ---------------------------------------------------------------------------
// Recursive sanitization walker
// ---------------------------------------------------------------------------

/**
 * Deep-walk a parsed page object and sanitize it for OSS schema validation:
 *
 *   isProContentType(type)      → replace entire object with PRO_TYPE_STUB
 *   isOssTypeWithProExtension(type) → strip Pro augmentation keys, recurse rest
 *   Everything else             → recurse into arrays / object values
 */
function sanitizeNode(node: unknown): unknown {
  if (Array.isArray(node)) {
    return node.map(sanitizeNode);
  }
  if (node !== null && typeof node === 'object') {
    const obj = node as Record<string, unknown>;
    const typeVal = obj['type'];

    if (typeof typeVal === 'string') {
      if (isProContentType(typeVal)) {
        // Entire node is a Pro type — replace with valid OSS stub.
        // We don't recurse into children; they're gone with the stub.
        return { ...PRO_TYPE_STUB };
      }
      if (isOssTypeWithProExtension(typeVal)) {
        // OSS type with Pro augmentation — strip Pro keys, recurse rest.
        const sanitized: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(obj)) {
          if (!PRO_EXTENSION_STRIP_KEYS.has(k)) {
            sanitized[k] = sanitizeNode(v);
          }
        }
        return sanitized;
      }
    }

    // Generic object (e.g. the page root, content wrapper, column objects).
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      result[k] = sanitizeNode(v);
    }
    return result;
  }

  // Primitives pass through unchanged.
  return node;
}

// ---------------------------------------------------------------------------
// Handler (exported for direct testing — no MCP server needed)
// ---------------------------------------------------------------------------

export interface ProWritePageInput {
  projectRoot: string;
  slug: string;
  content: string;
  locale?: string;
}

export type McpTextContent = { type: 'text'; text: string };

export interface ProWritePageResult {
  content: McpTextContent[];
  isError?: true;
}

/**
 * Core handler for stackwright_pro_write_page.
 *
 * Algorithm:
 *   1. Validate locale format (BCP 47).
 *   2. Parse YAML — return parse error verbatim on failure.
 *   3. Deep-clone + sanitize for OSS validation.
 *   4. Re-dump sanitized object to YAML.
 *   5. writePage() in a temp dir — catches real schema errors.
 *   6. Cleanup temp dir (always, via finally).
 *   7. Write ORIGINAL YAML string to the real project path.
 */
export function handleProWritePage(input: ProWritePageInput): ProWritePageResult {
  const { projectRoot, slug, content, locale } = input;

  // Step 1 — locale format guard
  if (locale !== undefined && !BCP47_RE.test(locale)) {
    return {
      isError: true,
      content: [
        {
          type: 'text',
          text: `Invalid locale tag: "${locale}". Expected BCP 47 format (e.g. "fr", "en-US").`,
        },
      ],
    };
  }

  // Step 2 — YAML parse
  let parsed: unknown;
  try {
    parsed = yamlLoad(content);
  } catch (err) {
    return {
      isError: true,
      content: [
        {
          type: 'text',
          text: `YAML parse error: ${err instanceof Error ? err.message : String(err)}`,
        },
      ],
    };
  }

  // Step 3 — deep-clone via JSON round-trip, then sanitize
  const clone = JSON.parse(JSON.stringify(parsed)) as unknown;
  const sanitized = sanitizeNode(clone);

  // Step 4 — dump sanitized clone to YAML for writePage validation
  const sanitizedYaml = yamlDump(sanitized);

  // Step 5+6 — OSS validation via writePage in a temp dir
  const tmpDir = fs.mkdtempSync(path.join(os.tmpdir(), 'sw-pro-validate-'));
  try {
    writePage(path.join(tmpDir, 'pages'), '__validation__', sanitizedYaml);
  } catch (err) {
    // VALIDATION_FAILED or YAML_PARSE_ERROR — real user error, return verbatim
    return {
      isError: true,
      content: [
        {
          type: 'text',
          text: err instanceof Error ? err.message : String(err),
        },
      ],
    };
  } finally {
    fs.rmSync(tmpDir, { recursive: true, force: true });
  }

  // Step 7 — write ORIGINAL YAML to the real project path
  const pagesDir = resolvePagesDir(projectRoot);
  const cleanSlug = slug.replace(/^\//, '').replace(/\\/g, '/');

  if (locale !== undefined) {
    // Locale write: only write content.<locale>.yml — never touch content.yml
    const localePath = path.join(pagesDir, cleanSlug, `content.${locale}.yml`);
    const created = !fs.existsSync(localePath);
    fs.mkdirSync(path.dirname(localePath), { recursive: true });
    fs.writeFileSync(localePath, content, 'utf8');
    const verb = created ? 'Created' : 'Updated';
    return {
      content: [{ type: 'text', text: `${verb} Pro page "${slug}" [${locale}] at ${localePath}` }],
    };
  }

  // Default locale: content.yml
  const contentPath = path.join(pagesDir, cleanSlug, 'content.yml');
  const created = !fs.existsSync(contentPath);
  fs.mkdirSync(path.dirname(contentPath), { recursive: true });
  fs.writeFileSync(contentPath, content, 'utf8');
  const verb = created ? 'Created' : 'Updated';
  return {
    content: [{ type: 'text', text: `${verb} Pro page "${slug}" at ${contentPath}` }],
  };
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerProPageTools(server: McpServer): void {
  server.tool(
    'stackwright_pro_write_page',
    "Write or update a page's YAML content with Pro content-type awareness. Validates against the Stackwright schema (including Pro types like stats_grid, data_table_pulse, map_pulse, alert_banner). Invalid YAML or schema violations on OSS-type fields are rejected with field-level errors. Use this instead of stackwright_write_page for any page containing Pro content types.",
    {
      projectRoot: z.string().describe('Absolute path to the Stackwright project root'),
      slug: z.string().describe('Page slug (e.g. "dashboard/main" or "/dashboard/main")'),
      content: z.string().describe('Full YAML content for the page (content.yml contents)'),
      locale: z
        .string()
        .optional()
        .describe(
          'BCP 47 locale tag (e.g. "fr", "de"). If provided, writes content.<locale>.yml instead of content.yml.'
        ),
    },
    async ({ projectRoot, slug, content, locale }) => {
      return handleProWritePage({ projectRoot, slug, content, locale });
    }
  );
}
